import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import {
	ConsoleShell,
	SidebarNav,
	SidebarNavLink,
} from "../../components/console-shell.tsx";
import { ADMIN_SECTIONS } from "../../navigation.ts";

/**
 * The admin console.
 *
 * The guard lives on the layout route, so every screen added underneath is
 * protected by existing — the only kind of authorization check that does not
 * eventually get forgotten.
 *
 * It is a *redirect*, not a security control. Every procedure the console calls
 * authorises independently, and a hand-crafted request that never loads this
 * component gets nowhere. What this does is stop a signed-in member from
 * finding an empty, broken-looking page.
 */
export const Route = createFileRoute("/admin")({
	beforeLoad: async ({ context, location }) => {
		const session = await context.queryClient.ensureQueryData(
			context.trpc.session.queryOptions(),
		);

		if (session.user === null) {
			throw redirect({ to: "/sign-in", search: { redirect: location.href } });
		}

		// The same permission list the server computed, from the same rules in
		// `@nwga/auth/permissions`.
		if (!session.permissions.includes("admin.access")) {
			throw redirect({ to: "/dashboard" });
		}

		return { session };
	},
	component: AdminLayout,
});

function AdminLayout() {
	return (
		<ConsoleShell
			sidebar={
				<SidebarNav label="Administration">
					{ADMIN_SECTIONS.map((section) => (
						<SidebarNavLink
							key={section.to}
							to={section.to}
							label={section.label}
							icon={section.icon}
							exact={section.exact}
						/>
					))}
				</SidebarNav>
			}
		>
			<Outlet />
		</ConsoleShell>
	);
}
